/**
 * Subscription terms of Garagevo (accepted by the owner on 2026-11-02): plan prices, extra users,
 * yearly payment, the 30 day trial (the first 7 days Profi, then Alap; owner's decision, 2026-11-03) and the
 * founder offer, and the monthly amount to invoice.
 *
 * The price list lives in the database (public.platform_settings, key 'pricing'; the operator changes
 * it). DEFAULT_PRICING is the same list as the migration puts there (a database test keeps them equal).
 * Money: integers in forint, net prices, VAT per line, net + VAT = gross exactly.
 */
import { PLANS, isPlan, planIncludes, type Plan } from "./features";
import { vatFromNet, netFromGross } from "./vat";
import { addDays, addMonths, daysBetween } from "./reminders";

export type BillingPeriod = "monthly" | "yearly";
export const BILLING_PERIODS: readonly BillingPeriod[] = ["monthly", "yearly"];

export interface FounderOffer {
  /** How many workshops get it (the first ones by creation, test workshops left out). */
  limit: number;
  /** Discounted paid months. */
  months: number;
  /** Discount in percent. */
  percent: number;
  /** Their prices do not rise for this many months from the first paid day. */
  lockMonths: number;
  /** Their free trial in total (days, today included; owner's decision 2026-11-03: 60 instead of 30). */
  trialDays: number;
}

export interface Pricing {
  currency: "HUF";
  /** Net monthly price of each plan (forint). */
  plans: Record<Plan, number>;
  /** Net monthly price of one user above the plan's seats. */
  extraUserNet: number;
  /** VAT percent added to the net prices. */
  vatRate: number;
  /** Yearly payment: this many of the 12 months are free. */
  yearlyFreeMonths: number;
  /** Length of the free trial of a new workshop in total (days, today included; 0: no trial). */
  trialDays: number;
  /** The first days of the trial with a higher plan (the "Profi week"; 0: none). */
  trialUpgradeDays: number;
  /** The plan of those first days (Profi). */
  trialUpgradePlan: Plan;
  founder: FounderOffer;
}

/** Keep equal to the 'pricing' row of supabase/migrations/20261211000000_trial_pricing.sql, 20261215000000_trial_profi_week.sql and 20261216100000_founder_trial.sql. */
export const DEFAULT_PRICING: Pricing = {
  currency: "HUF",
  plans: { basic: 10000, medium: 19900, premium: 34900 },
  extraUserNet: 2000,
  vatRate: 27,
  yearlyFreeMonths: 2,
  trialDays: 30,
  trialUpgradeDays: 7,
  trialUpgradePlan: "medium",
  founder: { limit: 20, months: 6, percent: 50, lockMonths: 12, trialDays: 60 },
};

/** What each plan includes besides the price (shown on the plan cards; the database 'ai' settings decide). */
export const DEFAULT_PLAN_SEATS: Record<Plan, number> = { basic: 2, medium: 5, premium: 10 };
export const DEFAULT_PLAN_AI_CREDIT: Record<Plan, number> = { basic: 1000, medium: 3000, premium: 10000 };

const isInt = (v: unknown, min: number, max: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;

/** A price list as the database sends it (or the operator types it), checked; null when not valid. */
export function parsePricing(v: unknown): Pricing | null {
  if (!v || typeof v !== "object") return null;
  const p = v as Record<string, unknown>;
  const plans = p.plans as Record<string, unknown> | undefined;
  const founder = p.founder as Record<string, unknown> | undefined;
  if (!plans || typeof plans !== "object" || !PLANS.every((x) => isInt(plans[x], 0, 10_000_000))) return null;
  if (!founder || typeof founder !== "object") return null;
  if (!isInt(p.extraUserNet, 0, 10_000_000) || !isInt(p.vatRate, 0, 100) || !isInt(p.yearlyFreeMonths, 0, 11) || !isInt(p.trialDays, 0, 365)) return null;
  if (!isInt(p.trialUpgradeDays, 0, 365) || !isPlan(p.trialUpgradePlan)) return null;
  if (!isInt(founder.limit, 0, 100_000) || !isInt(founder.months, 0, 36) || !isInt(founder.percent, 0, 100) || !isInt(founder.lockMonths, 0, 60)) return null;
  // an older price list without it: the usual trial
  const founderTrial = founder.trialDays === undefined ? p.trialDays : founder.trialDays;
  if (!isInt(founderTrial, 0, 365)) return null;
  return {
    currency: "HUF",
    plans: { basic: plans.basic as number, medium: plans.medium as number, premium: plans.premium as number },
    extraUserNet: p.extraUserNet,
    vatRate: p.vatRate,
    yearlyFreeMonths: p.yearlyFreeMonths,
    trialDays: p.trialDays,
    trialUpgradeDays: p.trialUpgradeDays,
    trialUpgradePlan: p.trialUpgradePlan,
    founder: { limit: founder.limit, months: founder.months, percent: founder.percent, lockMonths: founder.lockMonths, trialDays: founderTrial },
  };
}

/** Prices kept for a founder (their price does not rise until priceLockUntil). */
export interface LockedPrices {
  plans: Record<Plan, number>;
  extraUserNet: number;
}

/** The subscription of one workshop (public.workshop_billing). Dates are YYYY-MM-DD. */
export interface SubscriptionState {
  /** The stored plan: what applies after the trial (the operator changes it; the owner chooses it during the trial). */
  plan: Plan;
  /** Last day of the free trial (null: no trial). */
  trialUntil: string | null;
  /** First day of the trial (null: no trial). */
  trialStarted: string | null;
  /** Last day of the Profi week at the start of the trial (null: none). */
  trialUpgradeUntil: string | null;
  /** The plan of the Profi week. */
  trialUpgradePlan: Plan | null;
  /** What the owner chose (during the trial or while read only after it; null: not chosen). */
  planAfterTrial: Plan | null;
  period: BillingPeriod;
  /** Users bought above the plan's seats. */
  extraSeats: number;
  founder: boolean;
  /** Last day of the founder discount. */
  founderUntil: string | null;
  /** Last day the founder's prices may not rise. */
  priceLockUntil: string | null;
  lockedPrices: LockedPrices | null;
  /** First paid day; the subscription periods start on this day of the month (null: on the 1st of every month). */
  billingAnchor: string | null;
  /** Operator status: "free" is never invoiced. */
  status: string;
  /** The operator's own net monthly plan price (null: the price list). */
  monthlyFee: number | null;
  /** The operator's own discount in percent (null: none). */
  discountPct: number | null;
}

export const EMPTY_SUBSCRIPTION: SubscriptionState = {
  plan: "basic", trialUntil: null, trialStarted: null, trialUpgradeUntil: null, trialUpgradePlan: null, planAfterTrial: null, period: "monthly", extraSeats: 0, founder: false, founderUntil: null,
  priceLockUntil: null, lockedPrices: null, billingAnchor: null, status: "trial", monthlyFee: null, discountPct: null,
};

/** Within the trial on the given day (the last day included). */
export const inTrial = (s: Pick<SubscriptionState, "trialUntil">, today: string): boolean => !!s.trialUntil && today <= s.trialUntil;

/** Days of the trial left, today included (0 after the trial). */
export const trialDaysLeft = (s: Pick<SubscriptionState, "trialUntil">, today: string): number =>
  inTrial(s, today) ? daysBetween(today, s.trialUntil!) + 1 : 0;

/** The trial reminder (orange, with the plan choice) shows in its last days. */
export const TRIAL_REMINDER_DAYS = 7;

/**
 * Read only (owner's decision, 2026-11-02, ÁSZF 4.3): the trial ended and no plan was chosen (and the operator
 * did not settle it with another status). Everything stays visible and downloadable, nothing can be written
 * until the owner chooses a plan. Same as public.workshop_read_only.
 */
export const readOnly = (s: Pick<SubscriptionState, "trialUntil" | "planAfterTrial" | "status">, today: string): boolean =>
  !!s.trialUntil && today > s.trialUntil && !s.planAfterTrial && s.status === "trial";

type TrialWeek = Pick<SubscriptionState, "trialUntil" | "trialUpgradeUntil">;

/** Within the Profi week at the start of the trial (its last day included). */
export const inUpgradeWeek = (s: TrialWeek, today: string): boolean => inTrial(s, today) && !!s.trialUpgradeUntil && today <= s.trialUpgradeUntil;

/** Days of the Profi week left, today included (0 outside it). */
export const upgradeDaysLeft = (s: TrialWeek, today: string): number =>
  inUpgradeWeek(s, today) ? daysBetween(today, s.trialUpgradeUntil!) + 1 : 0;

/** The Profi week is over, but the free trial still runs (on Alap). */
export const upgradeWeekEnded = (s: TrialWeek, today: string): boolean => inTrial(s, today) && !!s.trialUpgradeUntil && today > s.trialUpgradeUntil;

/** The summary of what was used shows in the last days of the Profi week. */
export const TRIAL_SUMMARY_DAYS = 2;

/** Day of the trial (1 on the first day), null without a trial start. */
export const trialDay = (s: Pick<SubscriptionState, "trialStarted">, today: string): number | null =>
  s.trialStarted ? daysBetween(s.trialStarted, today) + 1 : null;

const higher = (a: Plan, b: Plan): Plan => (planIncludes(a, b) ? a : b);

/**
 * The plan that works now (same as public.effective_plan): in the trial the Profi week's plan during its days
 * (unless the stored plan is higher) and the stored plan (Alap) after them; read only after the trial the
 * Profi week's plan, so nothing made in the trial is hidden; the stored plan otherwise.
 */
export const effectivePlan = (s: Pick<SubscriptionState, "plan" | "trialUntil" | "trialUpgradeUntil" | "trialUpgradePlan" | "planAfterTrial" | "status">, today: string): Plan => {
  if (inTrial(s, today)) return inUpgradeWeek(s, today) && s.trialUpgradePlan ? higher(s.trialUpgradePlan, s.plan) : s.plan;
  if (readOnly(s, today) && s.trialUpgradePlan) return higher(s.trialUpgradePlan, s.plan);
  return s.plan;
};

/**
 * Choosing this plan during the trial ends it at once and starts billing today (Profi, Premium: their
 * features work at once). Alap is only recorded: the free days go on and billing starts after the trial.
 */
export const choiceEndsTrial = (plan: Plan): boolean => plan !== "basic";

/** The founder discount applies to a period starting on this day. */
export const founderActive = (s: Pick<SubscriptionState, "founder" | "founderUntil">, day: string): boolean => s.founder && !!s.founderUntil && day <= s.founderUntil;

/** Net monthly unit prices on a day: the founder's kept prices while they are lower, the operator's own plan price first. */
export function unitPrices(s: SubscriptionState, pricing: Pricing, day: string, plan: Plan = s.plan): { planNet: number; seatNet: number } {
  const locked = s.lockedPrices && s.priceLockUntil && day <= s.priceLockUntil ? s.lockedPrices : null;
  const listPlan = locked ? Math.min(locked.plans[plan] ?? pricing.plans[plan], pricing.plans[plan]) : pricing.plans[plan];
  const listSeat = locked ? Math.min(locked.extraUserNet, pricing.extraUserNet) : pricing.extraUserNet;
  return { planNet: s.monthlyFee ?? listPlan, seatNet: listSeat };
}

/** Discount of a month starting on this day: the larger of the founder discount and the operator's own (they do not add up). */
export function discountOn(s: SubscriptionState, pricing: Pricing, day: string): number {
  return Math.max(founderActive(s, day) ? pricing.founder.percent : 0, s.discountPct ?? 0);
}

const firstOfMonth = (iso: string) => `${iso.slice(0, 7)}-01`;
const lastOfMonth = (iso: string) => addDays(addMonths(firstOfMonth(iso), 1), -1);
const monthsBetween = (from: string, to: string) =>
  (Number(to.slice(0, 4)) - Number(from.slice(0, 4))) * 12 + (Number(to.slice(5, 7)) - Number(from.slice(5, 7)));

/**
 * The day a subscription period starts within the calendar month of `month`, or null when none does
 * (during the trial, before the first paid day, in the other 11 months of a yearly period, or "free").
 * Periods start on the anchor's day of the month; without an anchor on the 1st of every month.
 */
export function periodStartIn(s: SubscriptionState, month: string): string | null {
  if (s.status === "free") return null;
  // a trial workshop pays only once a plan is chosen (or the operator settled it)
  if (s.trialUntil && !s.planAfterTrial && s.status === "trial") return null;
  const first = firstOfMonth(month);
  const anchor = s.billingAnchor ?? first;
  if (anchor > lastOfMonth(first)) return null;
  const k = monthsBetween(anchor, first);
  if (k < 0) return null;
  if (s.period === "yearly" && k % 12 !== 0) return null;
  return addMonths(anchor, k);
}

/** The first period start on or after `today` (within the next 13 months), or null. */
export function nextPeriodStart(s: SubscriptionState, today: string): string | null {
  for (let i = 0; i <= 13; i++) {
    const start = periodStartIn(s, addMonths(firstOfMonth(today), i));
    if (start && start >= today) return start;
  }
  return null;
}

export type ChargeKind = "plan" | "extraSeats" | "sms" | "aiCredit";

export interface ChargeLine {
  kind: ChargeKind;
  /** Plan: months paid; extra seats: users × months; SMS and AI credit: 1. */
  quantity: number;
  /** Net before the discounts (plan and extra seats; equal to net for the others). */
  listNet: number;
  net: number;
  vat: number;
  gross: number;
}

export interface Charge {
  lines: ChargeLine[];
  net: number;
  vat: number;
  gross: number;
}

const line = (kind: ChargeKind, quantity: number, listNet: number, net: number, vatRate: number): ChargeLine => {
  const vat = vatFromNet(net, vatRate);
  return { kind, quantity, listNet, net, vat, gross: net + vat };
};

const total = (lines: ChargeLine[]): Charge => ({
  lines,
  net: lines.reduce((a, l) => a + l.net, 0),
  vat: lines.reduce((a, l) => a + l.vat, 0),
  gross: lines.reduce((a, l) => a + l.gross, 0),
});

/**
 * The subscription of one period starting on `start`: one month, or 12 months for the price of
 * 12 − yearlyFreeMonths. Each month gets its own discount (founder months at half price), and the
 * yearly reduction applies to the sum, so the founder discount and the free months never count twice.
 */
export function periodCharge(s: SubscriptionState, pricing: Pricing, start: string, plan: Plan = s.plan): Charge {
  const months = s.period === "yearly" ? 12 : 1;
  let planList = 0;
  let planNet = 0;
  let seatList = 0;
  let seatNet = 0;
  for (let i = 0; i < months; i++) {
    const day = addMonths(start, i);
    const u = unitPrices(s, pricing, day, plan);
    const keep = 100 - discountOn(s, pricing, day);
    planList += u.planNet;
    planNet += Math.round((u.planNet * keep) / 100);
    seatList += u.seatNet * s.extraSeats;
    seatNet += Math.round((u.seatNet * s.extraSeats * keep) / 100);
  }
  if (months === 12) {
    const paid = 12 - pricing.yearlyFreeMonths;
    planList = Math.round((planList * paid) / 12);
    planNet = Math.round((planNet * paid) / 12);
    seatList = Math.round((seatList * paid) / 12);
    seatNet = Math.round((seatNet * paid) / 12);
  }
  const lines = [line("plan", months, planList, planNet, pricing.vatRate)];
  if (s.extraSeats > 0) lines.push(line("extraSeats", s.extraSeats * months, seatList, seatNet, pricing.vatRate));
  return total(lines);
}

/** Usage billed with the subscription, as the program already counts it. */
export interface MonthUsage {
  /** This month's SMS sent by Garagevo (sms_messages.price; net). */
  smsNet: number;
  /** Extra AI credit bought this month (ai_credit_purchases; the credit is gross). Gift credit is never billed. */
  aiCreditGross: number;
}

/**
 * The amount to invoice for a calendar month: the subscription period starting in it (nothing during
 * the trial, nothing in the other months of a yearly period, nothing for "free"), plus the month's SMS
 * and bought AI credit (billed also during the trial: real costs the workshop accepted).
 */
export function monthlyCharge(s: SubscriptionState, pricing: Pricing, month: string, usage: MonthUsage = { smsNet: 0, aiCreditGross: 0 }): Charge & { periodStart: string | null } {
  const start = periodStartIn(s, month);
  const lines = start ? [...periodCharge(s, pricing, start).lines] : [];
  if (usage.smsNet > 0) lines.push(line("sms", 1, usage.smsNet, usage.smsNet, pricing.vatRate));
  if (usage.aiCreditGross > 0) {
    const net = netFromGross(usage.aiCreditGross, pricing.vatRate);
    lines.push({ kind: "aiCredit", quantity: 1, listNet: net, net, vat: usage.aiCreditGross - net, gross: usage.aiCreditGross });
  }
  return { ...total(lines), periodStart: start };
}

/** Net and gross of a net amount (for price tags). */
export const withVat = (net: number, vatRate: number) => {
  const vat = vatFromNet(net, vatRate);
  return { net, vat, gross: net + vat };
};

/** List price of a plan for one period (monthly, or the yearly price), without discounts. */
export function listPrice(pricing: Pricing, plan: Plan, period: BillingPeriod): number {
  return period === "yearly" ? pricing.plans[plan] * (12 - pricing.yearlyFreeMonths) : pricing.plans[plan];
}

/** Trial days of a new workshop: the founder's (never shorter than the usual), 0 when there is no trial (public.trial_days_for). */
export function trialDaysFor(pricing: Pricing, founder: boolean): number {
  if (pricing.trialDays <= 0) return 0;
  return founder ? Math.max(pricing.founder.trialDays, pricing.trialDays) : pricing.trialDays;
}

/** The operator's trial extension: 1 to 90 days (default 30), from the later of the trial end and today (public.op_extend_trial). */
export const TRIAL_EXTEND_DEFAULT_DAYS = 30;
export const TRIAL_EXTEND_MAX_DAYS = 90;
export function extendedTrialUntil(trialUntil: string, today: string, days: number): string {
  return addDays(trialUntil > today ? trialUntil : today, days);
}

/** First paid day, founder end and price lock end of a workshop created on `today` (same as the database trigger). */
export function newWorkshopTerms(pricing: Pricing, today: string, founder: boolean): Pick<SubscriptionState, "trialUntil" | "trialStarted" | "trialUpgradeUntil" | "trialUpgradePlan" | "billingAnchor" | "founder" | "founderUntil" | "priceLockUntil" | "lockedPrices"> {
  const days = trialDaysFor(pricing, founder);
  const trialUntil = days > 0 ? addDays(today, days - 1) : null;
  const anchor = trialUntil ? addDays(trialUntil, 1) : today;
  const week = trialUntil && pricing.trialUpgradeDays > 0;
  return {
    trialUntil,
    trialStarted: trialUntil ? today : null,
    trialUpgradeUntil: week ? addDays(today, Math.min(pricing.trialUpgradeDays, days) - 1) : null,
    trialUpgradePlan: week ? pricing.trialUpgradePlan : null,
    billingAnchor: anchor,
    founder,
    founderUntil: founder ? addDays(addMonths(anchor, pricing.founder.months), -1) : null,
    priceLockUntil: founder ? addDays(addMonths(anchor, pricing.founder.lockMonths), -1) : null,
    lockedPrices: founder ? { plans: { ...pricing.plans }, extraUserNet: pricing.extraUserNet } : null,
  };
}

/** What the workshop used since the trial started that the Alap package does not have (public.trial_usage). */
export interface TrialUsage {
  /** Quote links sent to customers. */
  quoteLinks: number;
  /** Stock movements (receipts, stocktake, parts used from stock). */
  stockMovements: number;
  /** Vehicles with a technical inspection or service reminder whose customer asked for reminders. */
  reminders: number;
}

/** Nothing of it was used: the Alap package is probably enough. */
export const trialUsageEmpty = (u: TrialUsage): boolean => u.quoteLinks + u.stockMovements + u.reminders === 0;
